import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { limit, orderBy, where, type QueryConstraint } from 'firebase/firestore';
import { AnimatePresence, motion } from 'motion/react';
import {
  AUDIT_ACTIONS,
  canOrg,
  DEFAULT_USER_STATUS,
  type AuditAction,
  type AuditLogEntry,
  type Project,
} from '@allemnii-pulse/shared';
import {
  fetchMergedPage,
  useDocument,
  useMergedCollections,
  type CollectionSource,
} from '../../lib/firestore.js';
import { useOrgProjects } from '../../lib/useOrgProjects.js';
import { useAuth } from '../auth/AuthProvider.js';
import { useOrgs } from '../orgs/OrgProvider.js';
import { Card } from '../../components/ui/Card.js';
import { Button } from '../../components/ui/Button.js';
import { Avatar, AvatarFallback } from '../../components/ui/Avatar.js';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/Select.js';
import {
  Dialog,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../../components/ui/Dialog.js';
import { UserName } from '../../components/UserName.js';
import { AvatarWithStatus } from '../../components/UserStatus.js';
import { formatUserName, initialsFor, isSystemActor, useUserProfile } from '../../lib/users.js';
import { diffObjects, type FieldDiff } from '../../lib/diff.js';
import { relativeTime } from '../../lib/time.js';

/** Human phrasing for the fixed audit-action vocabulary — see docs/PLAN.md §7.1/§7.2. Falls
 * back to the raw action string for anything not covered, so a new action never renders blank. */
const ACTION_LABEL: Partial<Record<AuditAction, string>> = {
  'org.created': 'created the organisation',
  'org.updated': 'updated the organisation',
  'org.deleted': 'deleted the organisation',
  'org.member_added': 'added a member',
  'org.member_removed': 'removed a member',
  'org.member_role_changed': 'changed a member role',
  'org.dev_preferences_updated': 'updated developer preferences',
  'project.created': 'created a project',
  'project.updated': 'updated the project',
  'project.member_added': 'added a project member',
  'project.member_removed': 'removed a project member',
  'project.member_role_changed': 'changed a project member role',
  'project.deleted': 'deleted a project',
  'document.version_created': 'saved a new document version',
  'document.validated_llm_output': 'accepted a document version',
  'questionnaire.completed': 'completed the TDD questionnaire',
  'tasks.generated': 'generated tasks',
  'tasks.validated': 'validated generated tasks',
  'task.field_changed': 'edited a task',
  'task.status_changed': 'changed a task status',
  'task.deleted': 'deleted a task',
  'task.assignment_changed': 'changed a task assignment',
  'task.comment_added': 'commented on a task',
  'task.issue_raised': 'raised a blocker',
  'task.issue_resolved': 'resolved a blocker',
  'deliverable.added': 'added a deliverable',
  'deliverable.deleted': 'removed a deliverable',
  'regeneration.created': 'started a regeneration review',
  'regeneration.proposal_decided': 'decided a regeneration proposal',
  'export.generated': 'exported a document',
  'allowlist.domain_added': 'added an allowlist domain',
  'allowlist.domain_removed': 'removed an allowlist domain',
  'user.first_sign_in': 'signed in for the first time',
  'user.sign_in_denied': 'was refused sign-in',
};

/** Action-type filter groups for the feed, keyed by the audit action's prefix (the part before
 * the dot). Built from AUDIT_ACTIONS so a newly added action lands in its group automatically.
 * `orgOnly` groups never reach the project activity mirror, so they're hidden on a project tab. */
const ACTION_CATEGORIES = [
  { key: 'tasks', label: 'Tasks', prefixes: ['task', 'tasks'] },
  { key: 'documents', label: 'Documents', prefixes: ['document', 'questionnaire', 'export'] },
  { key: 'deliverables', label: 'Deliverables', prefixes: ['deliverable'] },
  { key: 'regenerations', label: 'Regenerations', prefixes: ['regeneration'] },
  { key: 'projects', label: 'Projects', prefixes: ['project'] },
  {
    key: 'organisation',
    label: 'Organisation',
    prefixes: ['org', 'allowlist', 'mcp', 'user'],
    orgOnly: true,
  },
].map((c) => ({
  ...c,
  actions: AUDIT_ACTIONS.filter((a) => c.prefixes.includes(a.split('.')[0]!)),
}));

/** Resolves a projectId to its display name, reading straight from Firestore like the rest of
 * the app — only used where the feed spans multiple projects (the org-wide dashboard). */
function useProjectName(projectId: string | null): string | null {
  const { data } = useDocument<Project>(projectId ? `projects/${projectId}` : null);
  return data?.name ?? null;
}

/** One feed row. A single profile read backs both the avatar initials and the name so they
 * never disagree while the profile document is still loading. `showProject` is set on the
 * org-wide feed (no projectId filter), where the same row could belong to any project. */
function ActivityRow({
  entry: e,
  showProject,
  onSelect,
}: {
  entry: AuditLogEntry;
  showProject: boolean;
  onSelect: (entry: AuditLogEntry) => void;
}) {
  const { data: profile, loading: profileLoading } = useUserProfile(e.actorUid);
  const projectName = useProjectName(showProject ? e.projectId : null);
  const actorStatus =
    profileLoading || isSystemActor(e.actorUid) ? null : (profile?.status ?? DEFAULT_USER_STATUS);

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: -8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2, ease: 'easeOut' }}
      onClick={() => onSelect(e)}
      className="flex cursor-pointer items-start gap-3 p-3 transition-colors hover:bg-surface-2"
    >
      <AvatarWithStatus status={actorStatus}>
        <Avatar>
          <AvatarFallback>{initialsFor(e.actorUid, profile)}</AvatarFallback>
        </Avatar>
      </AvatarWithStatus>
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span className="text-sm wrap-break-word">
          <span className="font-medium">{formatUserName(e.actorUid, profile)}</span>{' '}
          {ACTION_LABEL[e.action] ?? e.action}
          {e.summary && <span className="text-muted"> — {e.summary}</span>}
        </span>
        <span className="text-xs text-muted wrap-break-word">
          {relativeTime(e.ts)}
          {showProject && projectName && (
            <>
              {' '}
              ·{' '}
              <Link
                to={`/projects/${e.projectId}`}
                className="hover:underline"
                onClick={(ev) => ev.stopPropagation()}
              >
                {projectName}
              </Link>
            </>
          )}{' '}
          · <span className="font-mono">{e.action}</span>
          {e.source !== 'ui' && (
            <span className="ml-1 rounded bg-surface-2 px-1 text-[10px]">{e.source}</span>
          )}
        </span>
      </div>
    </motion.div>
  );
}

function absoluteTime(iso: unknown): string {
  const date = (iso as { toDate?: () => Date })?.toDate?.() ?? new Date(String(iso));
  return date.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'medium' });
}

/** Maps an audit entry's `targetRef` (a Firestore path, e.g. "projects/p1/tasks/t4") to an
 * in-app route, when one exists — most targets (orgs, users, allowlist domains, exports) have
 * no dedicated page yet, so this returns null for anything not covered. */
function resolveTargetPath(targetRef: string): string | null {
  const [root, projectId, sub, subId] = targetRef.split('/');
  if (root !== 'projects' || !projectId) return null;
  if (!sub) return `/projects/${projectId}`;

  switch (sub) {
    case 'tasks':
      return subId ? `/projects/${projectId}/tasks/${subId}` : `/projects/${projectId}/tasks`;
    case 'documents':
      return subId ? `/projects/${projectId}/documents/${subId}/history` : null;
    case 'deliverables':
      return `/projects/${projectId}/deliverables`;
    case 'questionnaires':
      return `/projects/${projectId}/tdd`;
    case 'regenerations':
      return `/projects/${projectId}/regenerations`;
    case 'members':
      return `/projects/${projectId}/settings`;
    default:
      return null;
  }
}

function formatFieldValue(v: unknown): string {
  if (v === undefined || v === null) return '—';
  if (typeof v === 'string') return v;
  return JSON.stringify(v, null, 2);
}

function DetailDiffRow({ diff }: { diff: FieldDiff }) {
  if (diff.status === 'unchanged') return null;
  const badgeClass =
    diff.status === 'added'
      ? 'bg-[color-mix(in_srgb,var(--success)_15%,transparent)] text-success'
      : diff.status === 'removed'
        ? 'bg-[color-mix(in_srgb,var(--danger)_15%,transparent)] text-danger'
        : 'bg-accent-soft text-accent-soft-fg';

  return (
    <div className="flex flex-col gap-2 border-b border-border py-3 last:border-none">
      <div className="flex items-center gap-2">
        <span className="min-w-0 break-all font-mono text-xs font-semibold">{diff.path}</span>
        <span
          className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-medium uppercase ${badgeClass}`}
        >
          {diff.status}
        </span>
      </div>
      <div className="grid grid-cols-1 gap-3 text-sm sm:grid-cols-2">
        <div className="flex min-w-0 flex-col gap-1">
          <span className="text-[10px] font-medium uppercase text-muted">From</span>
          <pre className="whitespace-pre-wrap rounded-lg wrap-anywhere border border-border bg-bg p-2 text-xs">
            {formatFieldValue(diff.before)}
          </pre>
        </div>
        <div className="flex min-w-0 flex-col gap-1">
          <span className="text-[10px] font-medium uppercase text-muted">To</span>
          <pre className="whitespace-pre-wrap rounded-lg wrap-anywhere border border-border bg-bg p-2 text-xs">
            {formatFieldValue(diff.after)}
          </pre>
        </div>
      </div>
    </div>
  );
}

/** Full detail popup for one feed entry — the raw audit fields (`targetRef`, `ip`, `before`/
 * `after`) that the row's one-line summary has no room for. */
function ActivityDetailDialog({
  entry,
  onOpenChange,
}: {
  entry: AuditLogEntry | null;
  onOpenChange: (open: boolean) => void;
}) {
  const { data: profile } = useUserProfile(entry?.actorUid);
  const diffs = useMemo(
    () => (entry?.before && entry?.after ? diffObjects(entry.before, entry.after) : null),
    [entry],
  );
  const changedDiffs = diffs?.filter((d) => d.status !== 'unchanged') ?? [];
  const targetPath = entry ? resolveTargetPath(entry.targetRef) : null;

  return (
    <Dialog open={entry !== null} onOpenChange={onOpenChange} className="max-w-lg">
      {entry && (
        <div className="flex flex-col gap-4">
          <DialogHeader>
            <DialogTitle className="pr-6 wrap-break-word">
              {formatUserName(entry.actorUid, profile)} {ACTION_LABEL[entry.action] ?? entry.action}
            </DialogTitle>
            <DialogDescription>{absoluteTime(entry.ts)}</DialogDescription>
          </DialogHeader>

          {entry.summary && <p className="text-sm wrap-break-word">{entry.summary}</p>}

          <dl className="grid grid-cols-[auto_minmax(0,1fr)] gap-x-3 gap-y-1.5 text-xs">
            <dt className="text-muted">Action</dt>
            <dd className="font-mono">{entry.action}</dd>

            <dt className="text-muted">Target</dt>
            <dd className="break-all font-mono">
              {targetPath ? (
                <Link to={targetPath} className="text-accent hover:underline">
                  {entry.targetRef}
                </Link>
              ) : (
                entry.targetRef
              )}
            </dd>

            <dt className="text-muted">Source</dt>
            <dd>{entry.source}</dd>

            {entry.ip && (
              <>
                <dt className="text-muted">IP</dt>
                <dd className="font-mono">{entry.ip}</dd>
              </>
            )}
          </dl>

          {changedDiffs.length > 0 && (
            <div className="flex flex-col">
              <span className="mb-1 text-xs font-medium text-muted">Changes</span>
              <div className="flex flex-col divide-y divide-border">
                {changedDiffs.map((d) => (
                  <DetailDiffRow key={d.path} diff={d} />
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </Dialog>
  );
}

function tsMillis(ts: unknown): number {
  return (ts as { toMillis?: () => number })?.toMillis?.() ?? new Date(String(ts)).getTime();
}

const newestFirst = (a: AuditLogEntry, b: AuditLogEntry) => tsMillis(b.ts) - tsMillis(a.ts);

/** The furthest back the feed can page. Older entries stay in Firestore, just not in the feed. */
export const ACTIVITY_MAX_ITEMS = 200;

/**
 * Live activity feed, optionally narrowed to one actor. See docs/PLAN.md §7.2. Reused by both the
 * main dashboard (org-wide) and a project's Activity tab (projectId set). Where it reads from
 * follows the RBAC matrix (packages/shared/src/permissions.ts):
 *
 * - A project tab reads projects/{id}/activity — the IP-free mirror of that project's audit
 *   entries, which the rules hide from viewers (the tab itself is hidden from them too).
 * - The dashboard, for an org admin (org.audit.read), reads the org's full auditLogs.
 * - The dashboard, for anyone else, merges the activity of each project they can see and aren't
 *   a viewer on — no single query can, since each project's feed is gated on that project's role.
 *
 * Only the newest page is live. "Load older" pages back with a Firestore cursor on `ts`
 * (fetchMergedPage), up to ACTIVITY_MAX_ITEMS entries in all.
 */
export function ActivityFeed({
  projectId,
  pageSize = 20,
}: {
  projectId?: string;
  pageSize?: number;
}) {
  const { currentOrgId, currentOrg } = useOrgs();
  const { user } = useAuth();
  const { data: projects } = useOrgProjects();
  const [actorFilter, setActorFilter] = useState<string | null>(null);
  const [categoryFilter, setCategoryFilter] = useState<string | null>(null);
  const [selectedEntry, setSelectedEntry] = useState<AuditLogEntry | null>(null);
  const isOrgAdmin = canOrg(currentOrg?.role, 'org.audit.read');
  const categories = ACTION_CATEGORIES.filter((c) => !(projectId && c.orgOnly));
  const categoryActions = categories.find((c) => c.key === categoryFilter)?.actions ?? null;

  /** The feed's queries without a limit — the live first page and each older page add their own. */
  const baseSources = useMemo<CollectionSource[]>(() => {
    if (!currentOrgId || !user) return [];
    // Filtered in the query rather than client-side so a narrow filter still fills the page.
    // Each group stays well under Firestore's 30-value `in` limit.
    const tail: QueryConstraint[] = [
      ...(actorFilter ? [where('actorUid', '==', actorFilter)] : []),
      ...(categoryActions ? [where('action', 'in', categoryActions)] : []),
      orderBy('ts', 'desc'),
    ];
    if (projectId) return [{ path: `projects/${projectId}/activity`, constraints: tail }];
    if (isOrgAdmin) {
      return [{ path: 'auditLogs', constraints: [where('orgId', '==', currentOrgId), ...tail] }];
    }
    return projects
      .filter((p) => !(p.viewerUids ?? []).includes(user.uid))
      .map((p) => ({ path: `projects/${p.id}/activity`, constraints: tail }));
  }, [currentOrgId, user, projectId, isOrgAdmin, projects, actorFilter, categoryActions]);
  const liveSources = useMemo(
    () => baseSources.map((s) => ({ ...s, constraints: [...s.constraints, limit(pageSize)] })),
    [baseSources, pageSize],
  );

  // The org id is part of the key because the admin source's path ('auditLogs') is the same for
  // every org — only its where() differs.
  const sourcesKey = `${currentOrgId}|${baseSources.map((src) => src.path).join(',')}|${actorFilter ?? ''}|${categoryFilter ?? ''}|${pageSize}`;
  const { data: liveEntries, loading } = useMergedCollections<AuditLogEntry>(
    liveSources,
    sourcesKey,
    newestFirst,
    pageSize,
  );

  // Every entry shown so far, by id. Kept rather than recomputed from the live page so an entry
  // that new activity pushes off the live page doesn't leave a gap above the older pages.
  const [seen, setSeen] = useState<Record<string, AuditLogEntry>>({});
  const [olderExhausted, setOlderExhausted] = useState(false);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [olderError, setOlderError] = useState(false);
  // The filters an in-flight "Load older" was for, so a late response can't land in a feed
  // whose filters have since changed.
  const activeKeyRef = useRef(sourcesKey);

  // Declared before the merge below so a filter change clears first. The merge depends only on
  // the live entries, so it doesn't re-add the previous filter's entries on that same render.
  useEffect(() => {
    activeKeyRef.current = sourcesKey;
    setSeen({});
    setOlderExhausted(false);
    setOlderError(false);
  }, [sourcesKey]);
  useEffect(() => {
    if (liveEntries.length === 0) return;
    setSeen((prev) => {
      const next = { ...prev };
      for (const e of liveEntries) next[e.id] = e;
      return next;
    });
  }, [liveEntries]);

  const entries = useMemo(
    () => Object.values(seen).sort(newestFirst).slice(0, ACTIVITY_MAX_ITEMS),
    [seen],
  );
  const atCap = entries.length >= ACTIVITY_MAX_ITEMS;
  // A first page that isn't full means there's nothing older to fetch.
  const canLoadOlder =
    !loading && !olderExhausted && !atCap && liveEntries.length >= pageSize && entries.length > 0;

  async function loadOlder() {
    const oldest = entries[entries.length - 1];
    if (!oldest) return;
    const key = sourcesKey;
    setLoadingOlder(true);
    setOlderError(false);
    try {
      const { data, hasMore } = await fetchMergedPage<AuditLogEntry>(
        baseSources,
        oldest.ts,
        Math.min(pageSize, ACTIVITY_MAX_ITEMS - entries.length),
        newestFirst,
      );
      if (activeKeyRef.current !== key) return;
      setSeen((prev) => {
        const next = { ...prev };
        for (const e of data) next[e.id] = e;
        return next;
      });
      if (!hasMore) setOlderExhausted(true);
    } catch {
      if (activeKeyRef.current === key) setOlderError(true);
    } finally {
      if (activeKeyRef.current === key) setLoadingOlder(false);
    }
  }

  const actors = useMemo(() => Array.from(new Set(entries.map((e) => e.actorUid))), [entries]);

  return (
    <>
      <Card className="flex flex-col overflow-hidden">
        <div className="flex flex-wrap items-center gap-2 border-b border-border p-3">
          <span className="flex items-center gap-1.5 text-xs font-medium text-success">
            <span className="h-1.5 w-1.5 rounded-full bg-success" />
            Live
          </span>
          <Select
            value={categoryFilter ?? '__all__'}
            onValueChange={(v) => setCategoryFilter(v === '__all__' ? null : v)}
          >
            <SelectTrigger className="ml-auto h-9 w-auto gap-1 border-none bg-transparent px-2 text-xs sm:h-7">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">All activity</SelectItem>
              {categories.map((c) => (
                <SelectItem key={c.key} value={c.key}>
                  {c.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {/* Kept visible while a filter is set, since the filtered entries alone would leave only
              one actor and hide the way back to "Everyone". */}
          {(actors.length > 1 || actorFilter) && (
            <Select
              value={actorFilter ?? '__all__'}
              onValueChange={(v) => setActorFilter(v === '__all__' ? null : v)}
            >
              <SelectTrigger className="h-9 w-auto min-w-0 max-w-full gap-1 border-none bg-transparent px-2 text-xs sm:h-7">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">Everyone</SelectItem>
                {actors.map((a) => (
                  <SelectItem key={a} value={a}>
                    <UserName uid={a} />
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>

        {loading ? (
          <p className="p-4 text-sm text-muted">Loading…</p>
        ) : entries.length === 0 ? (
          <p className="p-8 text-center text-sm text-muted">
            {categoryFilter || actorFilter ? 'No matching activity.' : 'No activity yet.'}
          </p>
        ) : (
          <div className="flex flex-col divide-y divide-border">
            <AnimatePresence initial={false} mode="popLayout">
              {entries.map((e) => (
                <ActivityRow
                  key={e.id}
                  entry={e}
                  showProject={!projectId}
                  onSelect={setSelectedEntry}
                />
              ))}
            </AnimatePresence>
          </div>
        )}

        {!loading && entries.length > 0 && (canLoadOlder || atCap || olderError) && (
          <div className="flex flex-col items-center gap-1 border-t border-border p-3">
            {canLoadOlder && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-9 sm:h-7"
                disabled={loadingOlder}
                onClick={() => void loadOlder()}
              >
                {loadingOlder ? 'Loading…' : 'Load older'}
              </Button>
            )}
            {olderError && (
              <span className="text-xs text-danger">Couldn't load older activity. Try again.</span>
            )}
            {atCap && (
              <span className="text-xs text-muted">
                Showing the latest {ACTIVITY_MAX_ITEMS} entries.
              </span>
            )}
          </div>
        )}
      </Card>
      <ActivityDetailDialog
        entry={selectedEntry}
        onOpenChange={(open) => !open && setSelectedEntry(null)}
      />
    </>
  );
}
